import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../../../../api/axios';
import type {
  TCategoryListResponse,
  TCategoryResponse,
  TMenuCategory,
  TUpdateCategoryRequest,
} from '../types';

const CATEGORY_URL = '/menu/category';

// Shared with manage-menu-services to keep the cache consistent
export const CATEGORY_QUERY_KEY = 'menu-categories';

export const useGetCategoryList = (restaurantId: string) => {
  return useQuery({
    queryKey: [CATEGORY_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TCategoryListResponse>(
        `${CATEGORY_URL}?pageSize=100000&pageNum=1&filter__idRestaurant=${restaurantId}`,
        { headers: { 'Restaurant-ID': restaurantId } }
      );
      const rows = response.data?.data?.rows ?? [];
      return rows.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as TMenuCategory[];
    },
    enabled: !!restaurantId,
  });
};

export const useGetCategoryById = (id: string, restaurantId: string) => {
  return useQuery({
    queryKey: [CATEGORY_QUERY_KEY, restaurantId, id],
    queryFn: async () => {
      const response = await apiClient.get<TCategoryResponse>(`${CATEGORY_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    enabled: !!id && !!restaurantId,
  });
};

export const useUpdateCategory = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: TUpdateCategoryRequest }) => {
      const response = await apiClient.put<TCategoryResponse>(
        `${CATEGORY_URL}/${id}`,
        payload,
        { headers: { 'Restaurant-ID': restaurantId } }
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_QUERY_KEY, restaurantId] });
    },
  });
};

export const useDeleteCategory = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`${CATEGORY_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_QUERY_KEY, restaurantId] });
    },
  });
};
